import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import DocumentTable from './components/DocumentTable';
import DocumentFilters from './components/DocumentFilters';
import DocumentStats from './components/DocumentStats';
import BulkOperations from './components/BulkOperations';
import DocumentUploadModal from './components/DocumentUploadModal';
import DocumentPreviewModal from './components/DocumentPreviewModal';

const DocumentManagement = () => {
  const navigate = useNavigate();
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    transaction: '',
    documentType: '',
    signatureStatus: '',
    blockchainStatus: '',
    dateFrom: '',
    dateTo: ''
  });

  // Mock data
  const mockTransactions = [
    { id: 'TXN-2025-001', propertyAddress: '123 Sunset Blvd, Los Angeles, CA' },
    { id: 'TXN-2025-002', propertyAddress: '456 Ocean Ave, Santa Monica, CA' },
    { id: 'TXN-2025-003', propertyAddress: '789 Hollywood Blvd, Hollywood, CA' },
    { id: 'TXN-2025-004', propertyAddress: '321 Beverly Hills Dr, Beverly Hills, CA' },
    { id: 'TXN-2025-005', propertyAddress: '654 Malibu Point, Malibu, CA' }
  ];

  const mockDocuments = [
    {
      id: 'DOC-001',
      filename: 'Property_Deed_Sunset_Blvd.pdf',
      transactionId: 'TXN-2025-001',
      propertyAddress: '123 Sunset Blvd, Los Angeles, CA',
      type: 'deed',
      size: '2.4 MB',
      uploadDate: '2025-01-15T10:30:00Z',
      uploadedBy: 'Sarah Johnson (Lawyer)',
      signatureStatus: 'signed',
      blockchainVerified: true,
      versions: 3
    },
    {
      id: 'DOC-002',
      filename: 'Purchase_Agreement_Ocean_Ave.pdf',
      transactionId: 'TXN-2025-002',
      propertyAddress: '456 Ocean Ave, Santa Monica, CA',
      type: 'contract',
      size: '1.8 MB',
      uploadDate: '2025-01-18T14:15:00Z',
      uploadedBy: 'Michael Chen (Property Owner)',
      signatureStatus: 'pending',
      blockchainVerified: false,
      versions: 1
    },
    {
      id: 'DOC-003',
      filename: 'Property_Assessment_Hollywood.pdf',
      transactionId: 'TXN-2025-003',
      propertyAddress: '789 Hollywood Blvd, Hollywood, CA',
      type: 'assessment',
      size: '3.2 MB',
      uploadDate: '2025-01-20T09:45:00Z',
      uploadedBy: 'David Rodriguez (Engineer)',
      signatureStatus: 'pending',
      blockchainVerified: true,
      versions: 2
    },
    {
      id: 'DOC-004',
      filename: 'Title_Certificate_Beverly_Hills.pdf',
      transactionId: 'TXN-2025-004',
      propertyAddress: '321 Beverly Hills Dr, Beverly Hills, CA',
      type: 'certificate',
      size: '1.1 MB',
      uploadDate: '2025-01-22T16:20:00Z',
      uploadedBy: 'Lisa Thompson (Notary)',
      signatureStatus: 'signed',
      blockchainVerified: true,
      versions: 1
    },
    {
      id: 'DOC-005',
      filename: 'Inspection_Report_Malibu.pdf',
      transactionId: 'TXN-2025-005',
      propertyAddress: '654 Malibu Point, Malibu, CA',
      type: 'report',
      size: '4.7 MB',
      uploadDate: '2025-01-25T11:10:00Z',
      uploadedBy: 'Robert Kim (Engineer)',
      signatureStatus: 'rejected',
      blockchainVerified: false,
      versions: 1
    },
    {
      id: 'DOC-006',
      filename: 'Environmental_Report_Sunset.pdf',
      transactionId: 'TXN-2025-001',
      propertyAddress: '123 Sunset Blvd, Los Angeles, CA',
      type: 'report',
      size: '2.9 MB',
      uploadDate: '2025-01-26T13:30:00Z',
      uploadedBy: 'Emily Davis (Engineer)',
      signatureStatus: 'pending',
      blockchainVerified: false,
      versions: 1
    },
    {
      id: 'DOC-007',
      filename: 'Mortgage_Documents_Ocean.pdf',
      transactionId: 'TXN-2025-002',
      propertyAddress: '456 Ocean Ave, Santa Monica, CA',
      type: 'other',
      size: '1.5 MB',
      uploadDate: '2025-01-28T08:45:00Z',
      uploadedBy: 'James Wilson (Lawyer)',
      signatureStatus: 'expired',
      blockchainVerified: false,
      versions: 1
    }
  ];

  // Filter documents based on current filters
  const filteredDocuments = mockDocuments?.filter(document => {
    if (filters?.search && !document?.filename?.toLowerCase()?.includes(filters?.search?.toLowerCase()) &&
        !document?.transactionId?.toLowerCase()?.includes(filters?.search?.toLowerCase()) &&
        !document?.propertyAddress?.toLowerCase()?.includes(filters?.search?.toLowerCase())) {
      return false;
    }
    if (filters?.transaction && document?.transactionId !== filters?.transaction) return false;
    if (filters?.documentType && document?.type !== filters?.documentType) return false;
    if (filters?.signatureStatus && document?.signatureStatus !== filters?.signatureStatus) return false;
    if (filters?.blockchainStatus) {
      if (filters?.blockchainStatus === 'verified' && !document?.blockchainVerified) return false;
      if (filters?.blockchainStatus === 'pending' && document?.blockchainVerified) return false;
    }
    if (filters?.dateFrom && new Date(document.uploadDate) < new Date(filters.dateFrom)) return false;
    if (filters?.dateTo && new Date(document.uploadDate) > new Date(filters.dateTo)) return false;
    
    return true;
  });

  // Calculate statistics
  const documentStats = {
    totalDocuments: mockDocuments?.length,
    pendingSignatures: mockDocuments?.filter(doc => doc?.signatureStatus === 'pending')?.length,
    signedDocuments: mockDocuments?.filter(doc => doc?.signatureStatus === 'signed')?.length,
    blockchainVerified: mockDocuments?.filter(doc => doc?.blockchainVerified)?.length
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      transaction: '',
      documentType: '',
      signatureStatus: '',
      blockchainStatus: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  const handleSelectDocument = (documentId, isSelected) => {
    if (isSelected) {
      setSelectedDocuments(prev => [...prev, documentId]);
    } else {
      setSelectedDocuments(prev => prev?.filter(id => id !== documentId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedDocuments(filteredDocuments?.map(doc => doc?.id));
    } else {
      setSelectedDocuments([]);
    }
  };

  const handleBulkOperation = (operation, documentIds) => {
    console.log(`Executing ${operation} on documents:`, documentIds);
    
    switch (operation) {
      case 'request-signature':
        // Navigate to digital signature page with selected documents
        navigate('/digital-signature', { state: { documentIds } });
        break;
      case 'download':
        // Implement download functionality
        alert(`Downloading ${documentIds?.length} documents...`);
        break;
      case 'archive':
        // Implement archive functionality
        alert(`Archiving ${documentIds?.length} documents...`);
        break;
      case 'delete':
        // Implement delete functionality
        if (confirm(`Are you sure you want to delete ${documentIds?.length} documents?`)) {
          alert(`Deleting ${documentIds?.length} documents...`);
        }
        break;
      case 'share':
        // Implement share functionality
        alert(`Sharing ${documentIds?.length} documents...`);
        break;
      case 'export':
        // Implement export functionality
        alert(`Exporting ${documentIds?.length} documents to PDF...`);
        break;
      default:
        console.log('Unknown operation:', operation);
    }
    
    // Clear selection after operation
    setSelectedDocuments([]);
  };

  const handlePreviewDocument = (document) => {
    setPreviewDocument(document);
    setIsPreviewModalOpen(true);
  };

  const handleViewVersions = (document) => {
    console.log('Viewing versions for document:', document?.id);
    // Implement version history functionality
    alert(`Viewing version history for ${document?.filename}`);
  };

  const handleRequestSignature = (document) => {
    navigate('/digital-signature', { state: { documentId: document?.id } });
  };

  const handleUploadDocuments = (uploadData) => {
    console.log('Uploading documents:', uploadData);
    // Implement upload functionality
    alert(`Uploading ${uploadData?.files?.length} documents to transaction ${uploadData?.transactionId}`);
  };

  const handleClearSelection = () => {
    setSelectedDocuments([]);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Breadcrumbs />
      <main className="pt-32 pb-8">
        <div className="max-w-7xl mx-auto px-6 space-y-6">
          {/* Page Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Document Management</h1>
              <p className="text-muted-foreground mt-2">
                Centralized document operations across all transactions with blockchain verification
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => navigate('/reports-analytics')}
                iconName="BarChart3"
                iconPosition="left"
              >
                Analytics
              </Button>
              <Button
                variant="default"
                onClick={() => setIsUploadModalOpen(true)}
                iconName="Upload"
                iconPosition="left"
              >
                Upload Documents
              </Button>
            </div>
          </div>

          {/* Document Statistics */}
          <DocumentStats stats={documentStats} />

          {/* Document Filters */}
          <DocumentFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            documentCount={filteredDocuments?.length}
            transactions={mockTransactions}
          />

          {/* Bulk Operations */}
          <BulkOperations
            selectedDocuments={selectedDocuments}
            onBulkOperation={handleBulkOperation}
            onClearSelection={handleClearSelection}
          />

          {/* Document Table */}
          <DocumentTable
            documents={filteredDocuments}
            selectedDocuments={selectedDocuments}
            onSelectDocument={handleSelectDocument}
            onSelectAll={handleSelectAll}
            onPreviewDocument={handlePreviewDocument}
            onViewVersions={handleViewVersions}
            onRequestSignature={handleRequestSignature}
          />

          {/* Empty State */}
          {filteredDocuments?.length === 0 && (
            <div className="bg-card rounded-lg border border-border shadow-soft p-12 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="FileText" size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">No documents found</h3>
              <p className="text-muted-foreground mb-6">
                {Object.values(filters)?.some(value => value !== '') 
                  ? 'Try adjusting your filters to see more documents.' :'Upload your first document to get started with document management.'
                }
              </p>
              <div className="flex items-center justify-center space-x-3">
                {Object.values(filters)?.some(value => value !== '') && (
                  <Button
                    variant="outline"
                    onClick={handleClearFilters}
                    iconName="X"
                    iconPosition="left"
                  >
                    Clear Filters
                  </Button>
                )}
                <Button
                  variant="default"
                  onClick={() => setIsUploadModalOpen(true)}
                  iconName="Upload"
                  iconPosition="left"
                >
                  Upload Documents
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
      {/* Modals */}
      <DocumentUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUploadDocuments}
        transactions={mockTransactions}
      />
      <DocumentPreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        document={previewDocument}
      />
    </div>
  );
};

export default DocumentManagement;